import React from 'react';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Context } from '../../../models';

import { Table, Dropdown, TableCellDropdown, Message } from '@weezevent/nacre';

import ConfirmModal from '../../../components/confirmModal';

import Style from './operators.module.css';

const OperatorsRow = ({ operator, organizationId, flowId, moderationFlows, roles, onSelect, selected, syncOperators }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const displayModerationInfo = React.useMemo(() => {
        const moderationRoles = operator.roles.map(role => {
            return Context.parse(role);
        });

        const moderationContextsCount = new Set(moderationRoles.map(context => context.value)).size;

        if (!moderationFlows) {
            return '-';
        }
        switch (true) {
            case moderationRoles.some(context => (context.value === '*' && context.level === 5) || context.level === 3):
                return t('ticket.moderation.operators.list.allAccess');
            case moderationContextsCount === 1: {
                const matchingFlow = moderationFlows.find(flow => flow.id === moderationRoles[0].value);
                return matchingFlow ? matchingFlow.name : '-';
            }
            default:
                return t('ticket.moderation.operators.list.count.moderations', { count: moderationContextsCount.toString() });
        }
    }, [operator.roles, organizationId, t, moderationFlows]);

    const operatorRoles = React.useMemo(() => {
        switch (operator.roles.length) {
            case 0:
                return '-';
            case 1:
                return roles?.find(r => r.slug === operator.roles[0]?.role).name;
            default:
                return t('ticket.moderation.operators.list.count.roles', { count: operator.roles.length });
        }
    }, [operator, roles]);

    const editOperatorUri = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation/moderation_users/${operator.username}`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_users/${operator.username}`;
    }, [organizationId, flowId, operator]);

    const onDelete = React.useCallback(() => {
        operator.id = operator.username;
        operator.delete({ organizationId: organizationId }).then(() => {
            setOpenDeleteModal(false);
            setIsSubmitting(false);
            syncOperators();
        });
    }, [operator, syncOperators]);

    return (
        <>
            {openDeleteModal && (
                <ConfirmModal
                    labelTitle={t('ticket.moderation.operators.deleteModal.title')}
                    labelBody={
                        <Message title="Important" type="warning">
                            {t('ticket.moderation.operators.deleteModal.description')}
                        </Message>
                    }
                    objectToSave={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onAccept={onDelete}
                    isSubmitting={isSubmitting}
                    disabled={isSubmitting}
                />
            )}
            <Table.Tr active={selected.includes(operator.username)} onSelected={() => onSelect(operator.username)}>
                <Table.Td>
                    {`${operator.first_name} ${operator.last_name}`}
                    <div className={Style.smallTableText}>{operator.email}</div>
                </Table.Td>
                <Table.Td>{displayModerationInfo}</Table.Td>
                <Table.Td>{operatorRoles}</Table.Td>
                <TableCellDropdown size="medium" className={Style['dropdown_actions']}>
                    <Dropdown.Item key={0} onClick={() => history.push(editOperatorUri)}>
                        {t('ticket.common.action.edit')}
                    </Dropdown.Item>
                    <Dropdown.Line />
                    <Dropdown.Item key={1} deleteItem onClick={() => setOpenDeleteModal(true)}>
                        <Trans i18nKey="ticket.common.action.delete">{t('ticket.common.action.delete')}</Trans>
                    </Dropdown.Item>
                </TableCellDropdown>
            </Table.Tr>
        </>
    );
};

export default OperatorsRow;
